import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, IconButton, Grid,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useApi } from '../../hooks/useApi';
import { countRows, phaseLabel, phaseColor, isRowClosed, rowStaleness, rowWaitDays } from './qbrUtils';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function isPastDue(ymd) {
  return ymd && ymd < todayYmd();
}

function statusColor(status) {
  switch (status) {
    case 'completed': return 'success';
    case 'in progress':
    case 'scheduled':
    case 'open':
    case 'held':
      return 'info';
    case 'not started':
    case 'not scheduled':
    case 'upcoming':
      return 'default';
    default: return 'default';
  }
}

function Hub() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [cycles, setCycles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const [c, p, o] = await Promise.all([
        jApi.request('GET', 'cma/qbrcycle'),
        jApi.request('GET', 'cma/qbrprogram'),
        jApi.request('GET', 'cma/qbropportunity'),
      ]);
      setCycles(Array.isArray(c.data) ? c.data : []);
      setPrograms(Array.isArray(p.data) ? p.data : []);
      setOpportunities(Array.isArray(o.data) ? o.data : []);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load hub data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const accountManagerByAccount = useMemo(() => {
    const m = new Map();
    for (const p of programs) m.set(p.account, p.account_manager || '');
    return m;
  }, [programs]);

  // Counts by qbr_status
  const counts = useMemo(() => {
    const tally = { upcoming: 0, open: 0, completed: 0 };
    for (const c of cycles) {
      const k = c.qbr_status || 'upcoming';
      tally[k] = (tally[k] || 0) + 1;
    }
    return tally;
  }, [cycles]);

  // Audit-status view: upcoming cycles whose audit isn't completed,
  // with audit_notification_date past-due OR within next 30 days.
  const auditStatusRows = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 30);
    const cutoffYmd = cutoff.toISOString().slice(0, 10);
    return cycles
      .filter(c => c.qbr_status !== 'completed')
      .filter(c => c.audit_status !== 'completed')
      .filter(c => c.audit_notification_date && c.audit_notification_date <= cutoffYmd)
      .sort((a, b) => (a.audit_notification_date || '').localeCompare(b.audit_notification_date || '')
                   || (a.customer || '').localeCompare(b.customer || ''));
  }, [cycles]);

  // Scheduling view: upcoming cycles, meeting not yet held, with
  // meeting_notification_date past-due or within next 30 days.
  const schedulingRows = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 30);
    const cutoffYmd = cutoff.toISOString().slice(0, 10);
    return cycles
      .filter(c => c.qbr_status !== 'completed')
      .filter(c => c.meeting_status !== 'held')
      .filter(c => c.meeting_notification_date && c.meeting_notification_date <= cutoffYmd)
      .sort((a, b) => (a.meeting_notification_date || '').localeCompare(b.meeting_notification_date || '')
                   || (a.customer || '').localeCompare(b.customer || ''));
  }, [cycles]);

  // Cross-customer Outstanding view — every open audit issue / action item /
  // opportunity across every cycle. Sorted stalest-first so the vCIO sees
  // long-waiting items at the top. This is the "what's still in flight
  // anywhere?" panel.
  const sortStale = (a, b) => rowWaitDays(b) - rowWaitDays(a);

  const allOpenAuditIssues = useMemo(() => {
    const out = [];
    for (const c of cycles) {
      for (const row of (c.audit_issues || [])) {
        if (!row || isRowClosed(row)) continue;
        out.push({
          ...row,
          customer:      c.customer,
          account:       c.account,
          qbr_cycle:     c.qbr_cycle,
          qbrcycle_id:   c.id,
          qbrprogram_id: c.qbrprogram_id,
          meeting_date:  c.meeting_date,
        });
      }
    }
    out.sort(sortStale);
    return out;
  }, [cycles]);

  const allOpenActionItems = useMemo(() => {
    const out = [];
    for (const c of cycles) {
      for (const row of (c.action_items || [])) {
        if (!row || isRowClosed(row)) continue;
        out.push({
          ...row,
          customer:      c.customer,
          account:       c.account,
          qbr_cycle:     c.qbr_cycle,
          qbrcycle_id:   c.id,
          qbrprogram_id: c.qbrprogram_id,
          meeting_date:  c.meeting_date,
        });
      }
    }
    out.sort(sortStale);
    return out;
  }, [cycles]);

  const allOpenOpportunities = useMemo(
    () => opportunities
      .filter(o => !o.closed_date)
      .sort((a, b) => (b.created || 0) - (a.created || 0)),
    [opportunities]
  );

  const totalOpen = allOpenAuditIssues.length + allOpenActionItems.length + allOpenOpportunities.length;

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/qbr" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>QBR</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Hub</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  QBR Hub
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isLoading ? 'Loading…' : `${cycles.length} cycle${cycles.length === 1 ? '' : 's'} across ${programs.length} customer${programs.length === 1 ? '' : 's'}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={fetchData} disabled={isLoading}>
                <RefreshIcon/>
              </IconButton>
            </Stack>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {/* Status counts */}
        {!isLoading && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(counts).map(([status, n]) => (
              <Grid key={status} size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="overline" color="text.secondary">{status}</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>{n}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Audit status table */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
            Audits — past-due & next 30 days
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Audit Notify</TableCell>
                  <TableCell>Audit Complete By</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>AM</TableCell>
                  <TableCell>Audit Ticket</TableCell>
                  <TableCell>Phase</TableCell>
                  <TableCell align="right">Open / Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditStatusRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                    Nothing in the audit window.
                  </TableCell></TableRow>
                )}
                {auditStatusRows.map(c => {
                  const issues = countRows(c.audit_issues);
                  return (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer',
                      ...(isPastDue(c.meeting_notification_date) && { '& td': { color: 'error.main', fontWeight: 700 } })
                    }}
                    onClick={() => navigate(`/qbr/cycle/${c.id}`)}>
                    <TableCell>{c.audit_notification_date}</TableCell>
                    <TableCell>{c.meeting_notification_date}</TableCell>
                    <TableCell>{c.customer} <Typography component="span" variant="caption" color="text.secondary">({c.qbr_cycle})</Typography></TableCell>
                    <TableCell>{accountManagerByAccount.get(c.account) || '—'}</TableCell>
                    <TableCell>{c.audit_ticket || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={phaseLabel(c.phase)} color={phaseColor(c.phase)}/>
                    </TableCell>
                    <TableCell align="right">
                      {issues.total === 0
                        ? <Typography component="span" variant="caption" color="text.secondary">—</Typography>
                        : <Chip size="small"
                            label={`${issues.open} / ${issues.total}`}
                            color={issues.open > 0 ? 'warning' : 'success'}
                            variant={issues.open > 0 ? 'filled' : 'outlined'}/>}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Scheduling table */}
        <Box sx={{ mt: 3, pb: 4 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
            Scheduling — past-due & next 30 days
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Meeting Notify</TableCell>
                  <TableCell>Meeting Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>AM</TableCell>
                  <TableCell>Meeting Status</TableCell>
                  <TableCell>Phase</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedulingRows.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                    Nothing in the scheduling window.
                  </TableCell></TableRow>
                )}
                {schedulingRows.map(c => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer',
                      ...(isPastDue(c.meeting_notification_date) && { '& td': { color: 'error.main', fontWeight: 700 } })
                    }}
                    onClick={() => navigate(`/qbr/cycle/${c.id}`)}>
                    <TableCell>{c.meeting_notification_date}</TableCell>
                    <TableCell>{c.meeting_date}</TableCell>
                    <TableCell>{c.customer} <Typography component="span" variant="caption" color="text.secondary">({c.qbr_cycle})</Typography></TableCell>
                    <TableCell>{accountManagerByAccount.get(c.account) || '—'}</TableCell>
                    <TableCell><Chip size="small" label={c.meeting_status} color={statusColor(c.meeting_status)}/></TableCell>
                    <TableCell><Chip size="small" label={phaseLabel(c.phase)} color={phaseColor(c.phase)}/></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Cross-customer Outstanding — every open item across every cycle.
            The vCIO's "what's still in flight anywhere?" view. Three
            sub-tables, same shape as Program/Detail's Outstanding section
            but with a Customer column added. Sorted stalest-first. */}
        <Box sx={{ mt: 3, pb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
              Outstanding across all customers
            </Typography>
            <Chip size="small"
              label={`${totalOpen} open total`}
              color={totalOpen > 0 ? 'warning' : 'success'}
              variant={totalOpen > 0 ? 'filled' : 'outlined'}/>
          </Stack>

          {/* Open audit issues */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
              AUDIT ISSUES
            </Typography>
            <Chip size="small" label={allOpenAuditIssues.length}
              color={allOpenAuditIssues.length > 0 ? 'warning' : 'default'}
              variant={allOpenAuditIssues.length > 0 ? 'filled' : 'outlined'}/>
          </Stack>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Customer</TableCell>
                  <TableCell sx={{ width: 100 }}>Cycle</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{ width: 160 }}>Ticket #</TableCell>
                  <TableCell sx={{ width: 140 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Age</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allOpenAuditIssues.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                    No open audit issues anywhere.
                  </TableCell></TableRow>
                )}
                {allOpenAuditIssues.map((r, i) => {
                  const s = rowStaleness(r);
                  const days = rowWaitDays(r);
                  return (
                    <TableRow key={`${r.qbrcycle_id}-ai-${i}`} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/qbr/cycle/${r.qbrcycle_id}`)}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.customer}</TableCell>
                      <TableCell><code>{r.qbr_cycle}</code></TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell>{r.ticket || '—'}</TableCell>
                      <TableCell>{r.status || '—'}</TableCell>
                      <TableCell align="right">
                        {days > 0
                          ? <Chip size="small"
                              label={`${days}d`}
                              color={s.stale ? 'error' : 'default'}
                              variant={s.stale ? 'filled' : 'outlined'}/>
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Open action items */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
              ACTION ITEMS
            </Typography>
            <Chip size="small" label={allOpenActionItems.length}
              color={allOpenActionItems.length > 0 ? 'warning' : 'default'}
              variant={allOpenActionItems.length > 0 ? 'filled' : 'outlined'}/>
          </Stack>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Customer</TableCell>
                  <TableCell sx={{ width: 100 }}>Cycle</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{ width: 160 }}>Ticket #</TableCell>
                  <TableCell sx={{ width: 140 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Age</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allOpenActionItems.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                    No open action items anywhere.
                  </TableCell></TableRow>
                )}
                {allOpenActionItems.map((r, i) => {
                  const s = rowStaleness(r);
                  const days = rowWaitDays(r);
                  return (
                    <TableRow key={`${r.qbrcycle_id}-act-${i}`} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/qbr/cycle/${r.qbrcycle_id}`)}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.customer}</TableCell>
                      <TableCell><code>{r.qbr_cycle}</code></TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell>{r.ticket || '—'}</TableCell>
                      <TableCell>{r.status || '—'}</TableCell>
                      <TableCell align="right">
                        {days > 0
                          ? <Chip size="small"
                              label={`${days}d`}
                              color={s.stale ? 'error' : 'default'}
                              variant={s.stale ? 'filled' : 'outlined'}/>
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Open opportunities */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
              OPPORTUNITIES
            </Typography>
            <Chip size="small" label={allOpenOpportunities.length}
              color={allOpenOpportunities.length > 0 ? 'info' : 'default'}
              variant={allOpenOpportunities.length > 0 ? 'filled' : 'outlined'}/>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Customer</TableCell>
                  <TableCell sx={{ width: 100 }}>From cycle</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{ width: 140 }}>Opportunity #</TableCell>
                  <TableCell sx={{ width: 140 }}>Stage</TableCell>
                  <TableCell sx={{ width: 140 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allOpenOpportunities.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                    No open opportunities anywhere.
                  </TableCell></TableRow>
                )}
                {allOpenOpportunities.map(o => (
                  <TableRow key={o.id} hover sx={{ cursor: o.originating_qbrcycle_id ? 'pointer' : 'default' }}
                    onClick={() => o.originating_qbrcycle_id && navigate(`/qbr/cycle/${o.originating_qbrcycle_id}`)}>
                    <TableCell sx={{ fontWeight: 600 }}>{o.customer}</TableCell>
                    <TableCell><code>{o.originating_qbr_cycle || '—'}</code></TableCell>
                    <TableCell>{o.description}</TableCell>
                    <TableCell>{o.opportunity || '—'}</TableCell>
                    <TableCell>{o.stage || '—'}</TableCell>
                    <TableCell>{o.status || 'open'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
    </Box>
  );
}

export default Hub;
